import { Children, isValidElement, type ReactNode } from 'react';
import { useExperiment } from '@/lib/analytics/hooks';

export interface ExperimentProps {
  experimentKey: string;
  /** Used when PostHog has not returned a variant yet (`undefined`). */
  defaultVariant?: string;
  children: ReactNode;
}

export interface ExperimentVariantProps {
  name: string;
  children: ReactNode;
}

function ExperimentVariant({ children }: ExperimentVariantProps) {
  return <>{children}</>;
}

/**
 * Declarative A/B rendering: wrap `Experiment.Variant` children; only the branch
 * whose `name` matches the resolved variant (or `defaultVariant` while loading) renders.
 */
function ExperimentRoot({ experimentKey, defaultVariant = 'control', children }: ExperimentProps) {
  const variant = useExperiment(experimentKey);
  // PostHog often returns snake_case keys; `Variant` `name` props use kebab-case in app code.
  const resolved =
    variant !== undefined ? String(variant).replace(/_/g, '-') : defaultVariant;

  return Children.map(children, (child) => {
    if (isValidElement<ExperimentVariantProps>(child) && child.type === ExperimentVariant) {
      return child.props.name === resolved ? child : null;
    }
    return child;
  });
}

export const Experiment = Object.assign(ExperimentRoot, { Variant: ExperimentVariant });
